import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import SectionLabel from "../components/SectionLabel";
import useDocumentTitle from "../hooks/useDocumentTitle";

const VALUE_PROPS = [
  {
    n: "01",
    title: "Strukturierte Konzepte",
    copy: "Statt Einzelprodukt-Suche entstehen vollständige Einrichtungslösungen — Stilserie, Paket, Raumset, Anfrage.",
  },
  {
    n: "02",
    title: "Klare Entscheidungslogik",
    copy: "Stilserien und Pakete bieten eine belastbare Entscheidungsgrundlage — vom Privatobjekt bis zur Hospitality-Einheit.",
  },
  {
    n: "03",
    title: "Wiederholbar & nachbestellbar",
    copy: "Raumsets ermöglichen Standardausstattung mit konsistenter Handschrift — auch über mehrere Einheiten hinweg.",
  },
  {
    n: "04",
    title: "Persönliche Abstimmung",
    copy: "Jedes Projekt wird individuell vorbereitet — Lieferung, Umfang, Anpassungen klären wir im persönlichen Gespräch.",
  },
];

const TARGET_CHIPS = [
  "Ferienwohnungen",
  "Ferienhäuser",
  "Apartments",
  "Serviced Apartments",
  "Kleinere Hotels",
  "Betreiberobjekte",
  "Refit-Projekte",
];

export default function UeberUns() {
  useDocumentTitle(
    "Über uns — Livinora Interior Concepts",
    "Livinora Interior Concepts ist ein gemeinsames Projekt der Wilhelm Schildmeyer GmbH & Co. KG und der WHP Digital Solutions GmbH — für schlüsselfertige Einrichtungskonzepte."
  );

  const openNora = () => window.dispatchEvent(new CustomEvent("livinora:open-nora"));

  return (
    <div data-testid="page-ueber-uns">
      {/* Hero */}
      <section className="pt-28 sm:pt-32 md:pt-44 pb-20 md:pb-28">
        <div className="container-livinora">
          <Reveal>
            <SectionLabel index="∞" text="Über Livinora" />
          </Reveal>
          <Reveal delay={60}>
            <h1 className="display-xl mt-8 text-[44px] sm:text-[60px] md:text-[80px] lg:text-[96px] text-balance max-w-5xl">
              Zwei Kompetenzen.
              <br className="hidden sm:block" /> Ein gemeinsames Ziel.
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="prose-livinora prose-livinora--lead mt-9 max-w-3xl">
              Livinora Interior Concepts verbindet industrielle Möbelkompetenz mit digitaler Projekt-
              und Vertriebslogik — für schlüsselfertige Einrichtungskonzepte in Ferienobjekten,
              Apartments und Betreiberimmobilien.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Intro */}
      <section className="section-pad border-t border-[color:var(--livinora-hairline)]">
        <div className="container-livinora grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <SectionLabel index="01" text="Idee" />
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={60}>
              <h2 className="serif text-3xl sm:text-4xl md:text-5xl leading-[1.04] text-balance">
                Einrichtung, die nicht bei einzelnen Produkten beginnt.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora mt-7">
                Livinora ist entstanden, um Einrichtung für Ferienwohnungen, Ferienhäuser, Apartments
                und kleinere Hospitality-Objekte einfacher, strukturierter und hochwertiger planbar zu
                machen. Statt einzelner Möbelstücke steht das vollständige Konzept im Mittelpunkt:
                Objekttyp, Stilserie, Paket, Raumset und Projektanfrage greifen ineinander.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <p className="prose-livinora mt-5">
                Viele Ferienobjekte und Apartments benötigen keine beliebige Möbelauswahl, sondern eine
                klare, wiederholbare und zugleich stilvolle Ausstattungslösung. Livinora übersetzt
                diesen Bedarf in vorkonfigurierte Stilserien, Ausstattungspakete und anfragbare
                Raumsets — für einen strukturierten Weg von der ersten Orientierung bis zur
                persönlichen Projektabstimmung.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="section-pad bg-[color:var(--livinora-paper-warm)] border-y border-[color:var(--livinora-hairline)]">
        <div className="container-livinora">
          <div className="max-w-2xl">
            <Reveal>
              <SectionLabel index="02" text="Die Partner" />
            </Reveal>
            <Reveal delay={60}>
              <h2 className="serif text-3xl sm:text-4xl md:text-5xl mt-8 leading-[1.04]">
                Möbelkompetenz trifft Plattformlogik.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora mt-7">
                Livinora ist ein gemeinsames Projekt von zwei Unternehmen, die jeweils komplementäre
                Stärken einbringen — von Material und Fertigung bis zur digitalen Projektabwicklung.
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <Reveal>
              <article className="card-depth p-7 sm:p-9 h-full" data-testid="partner-schildmeyer">
                <span className="micro-label">Möbel & Fertigung</span>
                <h3 className="display-xl text-[28px] sm:text-[32px] md:text-[36px] mt-5 leading-[1.05]">
                  Wilhelm Schildmeyer GmbH & Co. KG
                </h3>
                <p className="prose-livinora mt-6">
                  Wilhelm Schildmeyer steht für langjährige Möbel- und Fertigungskompetenz. Diese
                  Erfahrung bildet eine wichtige Grundlage für Einrichtungslösungen, bei denen
                  Materialität, Funktion, Qualität und Serienfähigkeit zusammenspielen.
                </p>
              </article>
            </Reveal>
            <Reveal delay={120}>
              <article className="card-depth card-depth--warm p-7 sm:p-9 h-full" data-testid="partner-whp">
                <span className="micro-label">Digital & Plattform</span>
                <h3 className="display-xl text-[28px] sm:text-[32px] md:text-[36px] mt-5 leading-[1.05]">
                  WHP Digital Solutions GmbH
                </h3>
                <p className="prose-livinora mt-6">
                  WHP Digital Solutions bringt digitale Konzept-, Marken- und Vertriebslogik in das
                  Projekt ein. Dazu gehören die Strukturierung der Customer Journey, digitale
                  Anfrageprozesse, Plattformdenken und die Übersetzung eines erklärungsbedürftigen
                  Angebots in eine verständliche digitale Erfahrung.
                </p>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      {/* What Livinora delivers */}
      <section className="section-pad">
        <div className="container-livinora">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 items-end">
            <div className="md:col-span-6">
              <Reveal>
                <SectionLabel index="03" text="Anspruch" />
              </Reveal>
              <Reveal delay={60}>
                <h2 className="serif text-3xl sm:text-4xl md:text-5xl mt-8 leading-[1.04] text-balance">
                  Was Livinora leisten soll.
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <Reveal delay={140}>
                <p className="prose-livinora">
                  Vier Haltungen, die zusammen die Plattform tragen — vom ersten Klick bis zum
                  bezugsfertigen Objekt.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-[color:var(--livinora-hairline)]">
            {VALUE_PROPS.map((p, i) => (
              <Reveal key={p.n} delay={i * 80}>
                <div className={`p-7 md:p-8 h-full border-b md:border-b-0 ${
                  i !== VALUE_PROPS.length - 1 ? "md:border-r" : ""
                } border-[color:var(--livinora-hairline)]`}>
                  <span className="text-[10.5px] uppercase tracking-[0.32em] text-[color:var(--livinora-stone-deep)]">{p.n}</span>
                  <h3 className="serif text-2xl mt-3 leading-tight">{p.title}</h3>
                  <p className="mt-4 text-sm text-[color:var(--livinora-muted)] leading-relaxed">{p.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Audience */}
      <section className="section-pad bg-[color:var(--livinora-paper-warm)] border-y border-[color:var(--livinora-hairline)]">
        <div className="container-livinora grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <SectionLabel index="04" text="Adressaten" />
            </Reveal>
            <Reveal delay={60}>
              <h2 className="serif text-3xl sm:text-4xl md:text-5xl mt-8 leading-[1.04] text-balance">
                Für wen Livinora gedacht ist.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={140}>
              <p className="prose-livinora">
                Livinora richtet sich an Eigentümer, Betreiber und Projektverantwortliche, die
                Ferienobjekte, Apartments oder kleinere Hospitality-Flächen effizient, modern und
                stilsicher ausstatten möchten. Das Angebot eignet sich sowohl für neue Objekte als
                auch für Refit- und Modernisierungsvorhaben.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-10 flex flex-wrap gap-2">
                {TARGET_CHIPS.map((t) => (
                  <span
                    key={t}
                    className="text-[11px] uppercase tracking-[0.2em] px-3 py-2 border border-[color:var(--livinora-hairline)] text-[color:var(--livinora-ink-soft)] bg-[color:var(--livinora-paper)]"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Aspiration */}
      <section className="section-pad">
        <div className="container-livinora max-w-3xl">
          <Reveal>
            <SectionLabel index="05" text="Haltung" />
          </Reveal>
          <Reveal delay={60}>
            <h2 className="display-xl mt-8 text-[34px] sm:text-[44px] md:text-[56px] leading-[1.04] text-balance">
              Modern, stilvoll, strukturiert.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="prose-livinora prose-livinora--lead mt-8">
              Livinora soll Orientierung geben, Entscheidungen vereinfachen und den Weg zur passenden
              Einrichtungslösung verkürzen. Der Anspruch ist eine Verbindung aus gestalterischer
              Klarheit, funktionaler Alltagstauglichkeit und professioneller Projektvorbereitung.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-pad bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] atmosphere-dark noise">
        <div className="container-livinora text-center">
          <Reveal>
            <span className="section-label section-label--inverse mx-auto">
              <span className="section-label-num">∞</span>
              <span className="section-label-rule" aria-hidden="true" />
              <span className="section-label-text">Starten</span>
            </span>
          </Reveal>
          <Reveal delay={60}>
            <h2 className="display-xl mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-[80px] leading-[1.02] text-balance max-w-4xl mx-auto">
              Sie planen ein Objekt?
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-7 max-w-2xl mx-auto text-[color:var(--livinora-stone)] leading-relaxed text-base md:text-[17px]">
              Starten Sie mit dem Konfigurator, entdecken Sie vorkonfigurierte Raumsets oder lassen Sie
              Ihre Anfrage persönlich vorbereiten.
            </p>
          </Reveal>
          <Reveal delay={220}>
            <div className="mt-12 flex flex-wrap justify-center gap-4">
              <Link
                to="/konfigurator"
                className="btn-livinora btn-magnetic bg-[color:var(--livinora-paper)] text-[color:var(--livinora-ink)] hover:bg-white"
                data-testid="ueber-uns-cta-konfigurator"
              >
                Projekt konfigurieren <span>→</span>
              </Link>
              <Link
                to="/shop"
                className="btn-livinora border border-[color:var(--livinora-stone)] text-[color:var(--livinora-paper)] hover:bg-[color:var(--livinora-paper)] hover:text-[color:var(--livinora-ink)]"
                data-testid="ueber-uns-cta-shop"
              >
                Raumsets ansehen
              </Link>
              <button
                type="button"
                onClick={openNora}
                className="btn-livinora border border-transparent text-[color:var(--livinora-paper)] hover:bg-[color:var(--livinora-paper)]/10"
                data-testid="ueber-uns-cta-nora"
              >
                Nora fragen
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
